'use client'

import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@/store'
import {
  type BiometricData,
  type DocumentData,
  type EnrollmentData,
  type EnrollmentStatus,
  type EnrollmentType,
  type PersonalDetailsData,
} from '@/types/enrollment.types'

interface EnrollmentState {
  // Current enrollment data
  currentEnrollment: EnrollmentData | null

  // UI state
  currentStep: number
  isLoading: boolean
  isSaving: boolean

  // Validation state
  stepValidation: {
    personalDetails: boolean
    documents: boolean
    biometrics: boolean
    verification: boolean
  }

  // Error handling
  errors: Record<string, string>

  // Biometric validation errors
  biometricValidationErrors: {
    face?: string[]
    fingerprint?: string[]
    iris?: string[]
  }

  // Draft management
  lastSavedAt: string | null
  hasUnsavedChanges: boolean

  // Session management
  sessionStartedAt: string | null
  sessionExpiresAt: string | null
}

const initialState: EnrollmentState = {
  currentEnrollment: null,
  currentStep: 0,
  isLoading: false,
  isSaving: false,
  stepValidation: {
    personalDetails: false,
    documents: false,
    biometrics: false,
    verification: false,
  },
  errors: {},
  biometricValidationErrors: {},
  lastSavedAt: null,
  hasUnsavedChanges: false,
  sessionStartedAt: null,
  sessionExpiresAt: null,
}

const enrollmentSlice = createSlice({
  name: 'enrollment',
  initialState,
  reducers: {
    // Initialize new enrollment
    initializeEnrollment: (
      state,
      action: PayloadAction<{
        type: EnrollmentType
        enrollmentId: string
        enrollmentNumber?: string
        userId: string
      }>
    ) => {
      const { type, enrollmentId, enrollmentNumber, userId } = action.payload
      const now = new Date()

      state.currentEnrollment = {
        id: enrollmentId,
        enrollmentNumber:
          enrollmentNumber ||
          `ETH-${now.getFullYear()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        type,
        status: 'draft',
        currentStep: 'personal_details',
        documents: [],
        biometrics: [],
        createdBy: userId,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
      }

      state.currentStep = 0
      state.sessionStartedAt = now.toISOString()
      state.sessionExpiresAt = new Date(now.getTime() + 30 * 60000).toISOString() // 30 min session
      state.hasUnsavedChanges = false
      state.errors = {}
    },

    // Load existing enrollment
    loadEnrollment: (state, action: PayloadAction<EnrollmentData>) => {
      state.currentEnrollment = action.payload
      state.hasUnsavedChanges = false
      state.errors = {}

      // Determine current step based on data
      if (
        action.payload.personalDetails &&
        action.payload.documents.length > 0 &&
        action.payload.biometrics.length > 0
      ) {
        state.currentStep = 3 // Verification
      } else if (action.payload.personalDetails && action.payload.documents.length > 0) {
        state.currentStep = 2 // Biometrics
      } else if (action.payload.personalDetails) {
        state.currentStep = 1 // Documents
      } else {
        state.currentStep = 0 // Personal Details
      }
    },

    // Save personal details
    savePersonalDetails: (state, action: PayloadAction<PersonalDetailsData>) => {
      if (state.currentEnrollment) {
        state.currentEnrollment.personalDetails = action.payload
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.currentEnrollment.currentStep = 'documents'
        state.currentEnrollment.status = 'in_progress'
        state.stepValidation.personalDetails = true
        state.hasUnsavedChanges = true
        state.errors = {}
      }
    },

    // Add document
    addDocument: (state, action: PayloadAction<DocumentData>) => {
      if (state.currentEnrollment) {
        state.currentEnrollment.documents.push(action.payload)
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.hasUnsavedChanges = true
      }
    },

    // Save multiple documents at once
    saveDocuments: (state, action: PayloadAction<DocumentData[]>) => {
      if (state.currentEnrollment) {
        state.currentEnrollment.documents = action.payload
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.stepValidation.documents = true
        state.hasUnsavedChanges = true
      }
    },

    // Remove document
    removeDocument: (state, action: PayloadAction<string>) => {
      if (state.currentEnrollment) {
        state.currentEnrollment.documents = state.currentEnrollment.documents.filter(
          (doc) => doc.id !== action.payload
        )
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.hasUnsavedChanges = true
      }
    },

    // Update document verification status
    updateDocumentStatus: (
      state,
      action: PayloadAction<{
        documentId: string
        status: 'pending' | 'verified' | 'rejected'
        notes?: string
      }>
    ) => {
      if (state.currentEnrollment) {
        const doc = state.currentEnrollment.documents.find(
          (d) => d.id === action.payload.documentId
        )
        if (doc) {
          doc.verificationStatus = action.payload.status
          if (action.payload.notes) {
            doc.verificationNotes = action.payload.notes
          }
          state.currentEnrollment.updatedAt = new Date().toISOString()
          state.hasUnsavedChanges = true
        }
      }
    },

    // Add biometric data
    addBiometric: (state, action: PayloadAction<BiometricData>) => {
      if (state.currentEnrollment) {
        state.currentEnrollment.biometrics.push(action.payload)
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.currentEnrollment.currentStep = 'verification'
        state.hasUnsavedChanges = true
      }
    },

    // Remove biometric data
    removeBiometric: (state, action: PayloadAction<string>) => {
      if (state.currentEnrollment) {
        state.currentEnrollment.biometrics = state.currentEnrollment.biometrics.filter(
          (bio) => bio.id !== action.payload
        )
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.hasUnsavedChanges = true
      }
    },

    // Save facial biometric specifically
    saveFacialBiometric: (state, action: PayloadAction<BiometricData>) => {
      if (state.currentEnrollment) {
        // Remove any existing facial biometric
        state.currentEnrollment.biometrics = state.currentEnrollment.biometrics.filter(
          (b) => b.type !== 'face'
        )
        // Add new facial biometric
        state.currentEnrollment.biometrics.push(action.payload)
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.hasUnsavedChanges = true
        state.stepValidation.biometrics = true
        logger.log('[EnrollmentSlice] Facial biometric saved')
      }
    },

    // Save fingerprint biometric specifically
    saveFingerprintBiometric: (state, action: PayloadAction<BiometricData>) => {
      if (state.currentEnrollment) {
        // Remove any existing fingerprint biometric
        state.currentEnrollment.biometrics = state.currentEnrollment.biometrics.filter(
          (b) => b.type !== 'fingerprint'
        )

        // Add the new fingerprint biometric
        state.currentEnrollment.biometrics.push(action.payload)
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.currentEnrollment.currentStep = 'biometrics'
        state.stepValidation.biometrics = true
        state.hasUnsavedChanges = true

        logger.log('[EnrollmentSlice] Fingerprint biometric saved', {
          quality: action.payload.quality,
          metadata: action.payload.metadata,
        })
      }
    },

    // Save iris biometric specifically
    saveIrisBiometric: (state, action: PayloadAction<BiometricData>) => {
      if (state.currentEnrollment) {
        // Remove any existing iris biometric
        state.currentEnrollment.biometrics = state.currentEnrollment.biometrics.filter(
          (b) => b.type !== 'iris'
        )

        // Add the new iris biometric
        state.currentEnrollment.biometrics.push(action.payload)
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.currentEnrollment.currentStep = 'biometrics'
        state.stepValidation.biometrics = true
        state.hasUnsavedChanges = true

        logger.log('[EnrollmentSlice] Iris biometric saved', {
          quality: action.payload.quality,
          metadata: action.payload.metadata,
        })
      }
    },

    // Update biometric verification status
    updateBiometricStatus: (
      state,
      action: PayloadAction<{
        type: 'fingerprint' | 'face' | 'iris'
        status: 'pending' | 'verified' | 'rejected'
      }>
    ) => {
      if (state.currentEnrollment) {
        const biometric = state.currentEnrollment.biometrics.find(
          (bio) => bio.type === action.payload.type
        )
        if (biometric) {
          biometric.verificationStatus = action.payload.status
          state.currentEnrollment.updatedAt = new Date().toISOString()
        }
        logger.log('[EnrollmentSlice] Biometric status updated:', action.payload)
      }
    },

    // Save all biometrics at once
    saveBiometrics: (state, action: PayloadAction<BiometricData[]>) => {
      if (state.currentEnrollment) {
        state.currentEnrollment.biometrics = action.payload
        state.currentEnrollment.updatedAt = new Date().toISOString()
        state.hasUnsavedChanges = true
        state.stepValidation.biometrics = action.payload.length > 0
        logger.log('[EnrollmentSlice] All biometrics saved:', action.payload.length)
      }
    },

    // Navigation
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload
    },

    nextStep: (state) => {
      if (state.currentStep < 3) {
        state.currentStep += 1
      }
    },

    previousStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1
      }
    },

    // Update enrollment status
    updateEnrollmentStatus: (state, action: PayloadAction<EnrollmentStatus>) => {
      if (state.currentEnrollment) {
        state.currentEnrollment.status = action.payload
        state.currentEnrollment.updatedAt = new Date().toISOString()

        if (action.payload === 'completed') {
          state.currentEnrollment.completedAt = new Date().toISOString()
        } else if (action.payload === 'pending_verification') {
          state.currentEnrollment.submittedAt = new Date().toISOString()
        }
      }
    },

    // Validation
    setStepValidation: (
      state,
      action: PayloadAction<{
        step: keyof typeof initialState.stepValidation
        isValid: boolean
      }>
    ) => {
      state.stepValidation[action.payload.step] = action.payload.isValid
    },

    // Error handling
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload
    },

    clearErrors: (state) => {
      state.errors = {}
      state.biometricValidationErrors = {}
    },

    // Biometric validation error handling
    setBiometricValidationErrors: (
      state,
      action: PayloadAction<{
        type: 'face' | 'fingerprint' | 'iris'
        errors: string[]
      }>
    ) => {
      state.biometricValidationErrors[action.payload.type] = action.payload.errors
    },

    clearBiometricValidationErrors: (
      state,
      action?: PayloadAction<'face' | 'fingerprint' | 'iris'>
    ) => {
      if (action?.payload) {
        delete state.biometricValidationErrors[action.payload]
      } else {
        state.biometricValidationErrors = {}
      }
    },

    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload
    },

    // Draft management
    markAsSaved: (state) => {
      state.lastSavedAt = new Date().toISOString()
      state.hasUnsavedChanges = false
    },

    // Session management
    extendSession: (state) => {
      if (state.sessionExpiresAt) {
        const newExpiry = new Date(Date.now() + 30 * 60000) // Extend by 30 minutes
        state.sessionExpiresAt = newExpiry.toISOString()
      }
    },

    // Clear enrollment
    clearEnrollment: (_state) => {
      return initialState
    },
  },
})

// Actions
export const {
  initializeEnrollment,
  loadEnrollment,
  savePersonalDetails,
  addDocument,
  saveDocuments,
  removeDocument,
  updateDocumentStatus,
  addBiometric,
  removeBiometric,
  saveFacialBiometric,
  saveFingerprintBiometric,
  saveIrisBiometric,
  updateBiometricStatus,
  saveBiometrics,
  setCurrentStep,
  nextStep,
  previousStep,
  updateEnrollmentStatus,
  setStepValidation,
  setErrors,
  clearErrors,
  setBiometricValidationErrors,
  clearBiometricValidationErrors,
  setLoading,
  setSaving,
  markAsSaved,
  extendSession,
  clearEnrollment,
} = enrollmentSlice.actions

// Selectors
export const selectCurrentEnrollment = (state: RootState) => state.enrollment.currentEnrollment
export const selectCurrentStep = (state: RootState) => state.enrollment.currentStep
export const selectIsLoading = (state: RootState) => state.enrollment.isLoading
export const selectIsSaving = (state: RootState) => state.enrollment.isSaving
export const selectEnrollmentErrors = (state: RootState) => state.enrollment.errors
export const selectBiometricValidationErrors = (state: RootState) =>
  state.enrollment.biometricValidationErrors
export const selectHasUnsavedChanges = (state: RootState) => state.enrollment.hasUnsavedChanges
export const selectStepValidation = (state: RootState) => state.enrollment.stepValidation
export const selectSessionExpiresAt = (state: RootState) => state.enrollment.sessionExpiresAt

// Computed selectors
export const selectIsStepValid = (step: number) => (state: RootState) => {
  const validation = state.enrollment.stepValidation
  switch (step) {
    case 0:
      return validation.personalDetails
    case 1:
      return state.enrollment.currentEnrollment?.documents.length! > 0
    case 2:
      return state.enrollment.currentEnrollment?.biometrics.length! > 0
    case 3:
      return validation.verification
    default:
      return false
  }
}

export const selectEnrollmentProgress = (state: RootState) => {
  const enrollment = state.enrollment.currentEnrollment
  if (!enrollment) return 0

  let progress = 0
  if (enrollment.personalDetails) progress += 25
  if (enrollment.documents.length > 0) progress += 25
  if (enrollment.biometrics.length > 0) progress += 25
  if (enrollment.status === 'completed') progress = 100
  else if (enrollment.status === 'pending_verification') progress = 90

  return progress
}

// Reducer
export default enrollmentSlice.reducer
